import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { emitLive } from '../live.jsx';
import { api, getToken } from '../api.js';
import { useAuth } from '../auth.jsx';

// One real-time connection per signed-in user (Socket.IO on /socket.io,
// signed in with the site's token). Socket.IO itself falls back to HTTP
// long-polling when WebSockets are blocked; if it can't connect at all
// within 8 s, the browser polls /api/chat/updates every 5 s instead.
// Pages subscribe to events (message, read, typing) with useChatEvents.
// Also: the unread badge (header + side panel) and the opt-in browser
// notification with a short sound when the tab isn't in focus.

const ChatCtx = createContext({ unread: 0, mode: 'off', subscribe: () => () => {}, emitTyping: () => {}, setUnread: () => {}, presence: {}, watch: () => () => {} });
export const useChat = () => useContext(ChatCtx);
const NOTIFY_KEY = 'pme_chat_notify';
const POLL_MS = 5000;

export function notificationsOn() { try { return localStorage.getItem(NOTIFY_KEY) === '1' && 'Notification' in window && Notification.permission === 'granted'; } catch { return false; } }
export async function setNotifications(on) {
  try {
    if (on && 'Notification' in window && Notification.permission !== 'granted') {
      if ((await Notification.requestPermission()) !== 'granted') return false;
    }
    localStorage.setItem(NOTIFY_KEY, on ? '1' : '0');
    return on;
  } catch { return false; }
}
function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type = 'sine'; o.frequency.setValueAtTime(880, ctx.currentTime); o.frequency.setValueAtTime(1320, ctx.currentTime + 0.09);
    g.gain.setValueAtTime(0.0001, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.32);
    setTimeout(() => ctx.close(), 600);
  } catch { /* no audio */ }
}

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [mode, setMode] = useState('off'); // websocket | polling (Socket.IO long-polling) | rest (5 s) | off
  const listeners = useRef(new Set());
  // Online status of the members on screen: { [userId]: { online, lastSeen } }.
  // Pages register the ids they show (watch); refreshed every minute and
  // live from Socket.IO "presence" events.
  const [presence, setPresence] = useState({});
  const watched = useRef(new Map()); // id → number of watchers
  const socketRef = useRef(null);
  const userRef = useRef(user);
  userRef.current = user;
  const isMember = user && user.role !== 'admin';
  const isAdmin = user?.role === 'admin' && !user?.impersonation;

  const dispatch = useCallback((type, payload) => {
    for (const fn of listeners.current) fn(type, payload);
    if (type === 'message') {
      const u = userRef.current;
      const m = payload.message;
      const mine = m.sender.role === 'member' ? m.sender.id === u?.id : u?.role === 'admin';
      if (!mine && notificationsOn() && (document.hidden || !document.hasFocus())) {
        try {
          const n = new Notification(m.sender.role === 'support' ? 'Équipe PME' : (m.sender.company || m.sender.name || 'PME Trade Canada'), {
            body: m.body || (m.attachments?.length ? `📎 ${m.attachments[0].name}` : '…'), tag: `chat-${payload.conversationId}`, icon: '/icon-192.png',
          });
          n.onclick = () => { window.focus(); window.location.assign(`${u?.role === 'admin' ? '/admin/messages' : '/account/messages'}/${payload.conversationId}`); };
        } catch { /* blocked */ }
        beep();
      }
    }
  }, []);

  useEffect(() => {
    if (!user) { setUnread(0); setMode('off'); return undefined; }
    let alive = true;
    let pollTimer = null;
    let cursor = null;
    const badge = () => api(isAdmin ? '/admin/chat/stats' : '/chat/unread').then((r) => { if (alive) setUnread(isAdmin ? r.supportUnread : r.total); }).catch(() => {});
    badge();
    const poll = async () => {
      try {
        const r = await api(`/chat/updates${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
        if (!alive) return;
        cursor = r.cursor;
        for (const m of r.messages) dispatch('message', m);
        for (const x of r.reads) dispatch('read', x);
        for (const x of r.typing) dispatch('typing', x);
        if (!isAdmin) setUnread(r.unread);
      } catch { /* offline */ }
      if (alive) pollTimer = setTimeout(poll, POLL_MS);
    };
    const startPolling = () => { if (!pollTimer && alive && !isAdmin) { setMode('rest'); poll(); } };
    const stopPolling = () => { clearTimeout(pollTimer); pollTimer = null; };

    const socket = io({ path: '/socket.io', auth: { token: getToken() }, transports: ['websocket', 'polling'], reconnectionDelayMax: 10000 });
    socketRef.current = socket;
    const fallback = setTimeout(() => { if (!socket.connected) startPolling(); }, 8000);
    socket.on('connect', () => {
      stopPolling();
      setMode(socket.io.engine.transport.name === 'websocket' ? 'websocket' : 'polling');
      socket.io.engine.on('upgrade', (t) => setMode(t.name === 'websocket' ? 'websocket' : 'polling'));
      badge();
    });
    socket.on('connect_error', () => { if (!socket.connected) startPolling(); });
    socket.on('disconnect', () => { setTimeout(() => { if (!socket.connected) startPolling(); }, 4000); });
    socket.on('message', (p) => dispatch('message', p));
    socket.on('read', (p) => dispatch('read', p));
    socket.on('typing', (p) => dispatch('typing', p));
    socket.on('unread', (p) => { if (!isAdmin) setUnread(p.total); });
    socket.on('teamUnread', (p) => { if (isAdmin) setUnread(p.total); });
    socket.on('presence', (p) => setPresence((cur) => ({ ...cur, [p.userId]: { online: p.online, lastSeen: p.lastSeen } })));
    // Live updates (live.jsx): new build, content changed, my account changed.
    socket.on('version', (p) => emitLive('version', p));
    socket.on('content:updated', (p) => emitLive('content', p));
    socket.on('account:updated', (p) => emitLive('account', p));
    return () => { alive = false; clearTimeout(fallback); stopPolling(); socket.close(); socketRef.current = null; };
  }, [user?.id, isAdmin, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const refreshPresence = useCallback(async (only) => {
    const ids = (only || [...watched.current.keys()]).filter(Boolean);
    if (!ids.length || !userRef.current) return;
    try {
      const r = await api(`/chat/presence?ids=${ids.join(',')}`);
      setPresence((cur) => { const next = { ...cur }; for (const id of ids) next[id] = r.byId[id] || null; return next; });
    } catch { /* offline */ }
  }, []);
  const watch = useCallback((ids) => {
    const fresh = ids.filter((id) => id && !watched.current.has(id));
    for (const id of ids) if (id) watched.current.set(id, (watched.current.get(id) || 0) + 1);
    if (fresh.length) refreshPresence(fresh);
    return () => { for (const id of ids) { const n = (watched.current.get(id) || 1) - 1; if (n > 0) watched.current.set(id, n); else watched.current.delete(id); } };
  }, [refreshPresence]);
  useEffect(() => {
    if (!user) return undefined;
    const tm = setInterval(() => refreshPresence(), 60 * 1000);
    return () => clearInterval(tm);
  }, [user, refreshPresence]);
  const lastTyping = useRef(0);
  const emitTyping = useCallback((conversationId, asTeam = false) => {
    if (Date.now() - lastTyping.current < 2500) return;
    lastTyping.current = Date.now();
    const s = socketRef.current;
    if (s?.connected) s.emit('typing', { conversationId, asTeam });
    else if (!asTeam) api('/chat/typing', { method: 'POST', body: { conversationId } }).catch(() => {});
  }, []);
  const value = useMemo(() => ({ unread, setUnread, mode, subscribe, emitTyping, enabled: !!user, isMember, presence, watch, refreshPresence }),
    [unread, mode, subscribe, emitTyping, user, isMember, presence, watch, refreshPresence]);
  return <ChatCtx.Provider value={value}>{children}</ChatCtx.Provider>;
}

export function useChatEvents(fn) {
  const { subscribe } = useChat();
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => subscribe((type, payload) => ref.current(type, payload)), [subscribe]);
}

// Online status of some members, kept fresh: { [id]: { online, lastSeen } | null }.
export function usePresence(ids) {
  const { presence, watch } = useChat();
  const key = [...new Set((ids || []).filter(Boolean))].sort((a, b) => a - b).join(',');
  useEffect(() => (key ? watch(key.split(',').map(Number)) : undefined), [key, watch]);
  return presence;
}
